import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Settings, ExternalLink, Globe, Cookie, X, Info, Check, ShieldAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Link } from "react-router";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { deleteCookiesByCategory } from "@/utils/analytics";
import { isNative } from "@/utils/platform";

declare const gtag: (...args: any[]) => void;

export type CookieSettings = {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

type ConsentRecord = {
  settings: CookieSettings;
  timestamp: string;
  userAgent: string;
  method: "banner" | "detailed" | "default";
  version: string;
  expiresAt: string;
};

export const CONSENT_VERSION = "1.1.0";
const CONSENT_EXPIRY_DAYS = 180;

const defaultSettings: CookieSettings = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

const saveCookiePreferences = (settings: CookieSettings, method: "banner" | "detailed" | "default") => {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);

  const consentRecord: ConsentRecord = {
    settings,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    method,
    version: CONSENT_VERSION,
    expiresAt: expiryDate.toISOString()
  };

  localStorage.setItem('cookieConsent', JSON.stringify(consentRecord));
  applyCookieSettings(settings);
  window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: settings }));
};

const applyCookieSettings = (settings: CookieSettings) => {
  if (!settings.analytics) deleteCookiesByCategory('analytics');
  if (!settings.marketing) deleteCookiesByCategory('marketing');
  if (!settings.functional) deleteCookiesByCategory('functional');

  if (typeof gtag === 'function') {
    gtag('consent', 'update', {
      'analytics_storage': settings.analytics ? 'granted' : 'denied',
      'ad_storage': settings.marketing ? 'granted' : 'denied',
      'ad_user_data': settings.marketing ? 'granted' : 'denied',
      'ad_personalization': settings.marketing ? 'granted' : 'denied',
      'functionality_storage': settings.functional ? 'granted' : 'denied',
      'personalization_storage': settings.functional ? 'granted' : 'denied',
      'security_storage': 'granted'
    });
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'consent_updated',
      analytics_consent: settings.analytics ? 'granted' : 'denied',
      marketing_consent: settings.marketing ? 'granted' : 'denied',
      functional_consent: settings.functional ? 'granted' : 'denied'
    });

    if (settings.analytics) {
      window.dataLayer.push({ event: 'analytics_consent_granted' });
      window.dataLayer.push({
        event: 'behavioral_analytics_consent_granted',
        timestamp: new Date().toISOString()
      });
    }

    if (settings.marketing) {
      window.dataLayer.push({ event: 'ad_consent_granted' });
      window.dataLayer.push({
        event: 'ad_storage_consent_granted',
        timestamp: new Date().toISOString()
      });
    }
  }
};

const deleteCookie = (name: string) => {
  const domains = ['', `.${window.location.hostname}`, window.location.hostname];
  const paths = ['/', '/en/', '/de/', ''];

  domains.forEach(domain => {
    paths.forEach(path => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domain ? '; domain=' + domain : ''}`;
    });
  });
};

const getCookiePreferences = (): ConsentRecord | null => {
  const saved = localStorage.getItem('cookieConsent');
  if (!saved) return null;

  const consent = JSON.parse(saved) as ConsentRecord;

  if (consent.expiresAt) {
    const expiryDate = new Date(consent.expiresAt);
    if (expiryDate < new Date()) {
      localStorage.removeItem('cookieConsent');
      return null;
    }
  }

  if (consent.version !== CONSENT_VERSION) {
    return null;
  }

  return consent;
};

const cookieDetails = {
  necessary: [
    {
      name: "cookieConsent",
      purpose: "Stores your cookie preferences (plus consent timestamp, version and your user-agent) as proof of consent under Art. 7(1) GDPR",
      duration: "180 days",
      provider: "BuyForS",
      category: "Necessary",
      legalBasis: "Legitimate Interest"
    },
    {
      name: "SESSION",
      purpose: "Maintains your secure login session",
      duration: "Session",
      provider: "BuyForS",
      category: "Necessary",
      legalBasis: "Legitimate Interest"
    },
    {
      name: "__cf_bm",
      purpose: "Set by Cloudflare to identify and block automated bot traffic; strictly necessary for platform security",
      duration: "30 minutes",
      provider: "Cloudflare",
      category: "Necessary",
      legalBasis: "Legitimate Interest",
      dataController: "Cloudflare, Inc.",
      privacyPolicy: "https://www.cloudflare.com/privacypolicy/"
    }
  ],
  functional: [
    {
      name: "language",
      purpose: "Remembers your language preference to provide you with localized content",
      duration: "1 year",
      provider: "BuyForS",
      category: "Functional",
      legalBasis: "Consent"
    },
    {
      name: "ui-settings",
      purpose: "Stores your interface customizations for better user experience",
      duration: "1 year",
      provider: "BuyForS",
      category: "Functional",
      legalBasis: "Consent"
    }
  ],
  analytics: [
    {
      name: "_ga",
      purpose: "Used by Google Analytics to distinguish unique users",
      duration: "2 years",
      provider: "Google",
      category: "Analytics",
      legalBasis: "Consent",
      dataController: "Google LLC",
      privacyPolicy: "https://policies.google.com/privacy"
    },
    {
      name: "_gid",
      purpose: "Used by Google Analytics to store and update a unique value for each visited page",
      duration: "24 hours",
      provider: "Google",
      category: "Analytics",
      legalBasis: "Consent",
      dataController: "Google LLC",
      privacyPolicy: "https://policies.google.com/privacy"
    },
    {
      name: "_gat",
      purpose: "Used by Google Analytics to throttle request rate",
      duration: "1 minute",
      provider: "Google",
      category: "Analytics",
      legalBasis: "Consent",
      dataController: "Google LLC",
      privacyPolicy: "https://policies.google.com/privacy"
    },
    {
      name: "_ga_*",
      purpose: "Used by Google Analytics to persist session state",
      duration: "2 years",
      provider: "Google",
      category: "Analytics",
      legalBasis: "Consent",
      dataController: "Google LLC",
      privacyPolicy: "https://policies.google.com/privacy"
    }
  ],
  marketing: [
    {
      name: "_gcl_au",
      purpose: "Used by Google AdSense for experimenting with advertisement efficiency",
      duration: "3 months",
      provider: "Google",
      category: "Marketing",
      legalBasis: "Consent",
      dataController: "Google LLC",
      privacyPolicy: "https://policies.google.com/privacy"
    },
    {
      name: "_gac_*",
      purpose: "Contains campaign related information for the user",
      duration: "90 days",
      provider: "Google",
      category: "Marketing",
      legalBasis: "Consent",
      dataController: "Google LLC",
      privacyPolicy: "https://policies.google.com/privacy"
    }
  ]
};

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetailedSettings, setShowDetailedSettings] = useState(false);
  const { language, setLanguage } = useLanguage();

  if (isNative()) return null;

  const content = {
    en: {
      title: "Your Privacy Choices",
      description: "This website uses cookies to improve your browsing experience. We require your explicit consent before storing non-essential cookies that may process your personal data.",
      necessaryOnly: "Accept only necessary",
      acceptAll: "Accept all",
      rejectAll: "Reject all",
      customize: "Customize settings",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service",
      cookieSettings: "Cookie Settings",
      moreInfo: "More information",
      saveChoices: "Save choices",
      consentExpiryNotice: "Your consent will expire after 6 months and you'll be asked again.",
      dataProcessingNotice: "Some cookies involve third-party data processing."
    },
    de: {
      title: "Ihre Datenschutzoptionen",
      description: "Diese Website verwendet Cookies, um Ihr Surferlebnis zu verbessern. Wir benötigen Ihre ausdrückliche Einwilligung, bevor wir nicht wesentliche Cookies speichern, die Ihre persönlichen Daten verarbeiten können.",
      necessaryOnly: "Nur notwendige akzeptieren",
      acceptAll: "Alle akzeptieren",
      rejectAll: "Alle ablehnen",
      customize: "Einstellungen anpassen",
      privacyPolicy: "Datenschutz",
      termsOfService: "Nutzungsbedingungen",
      cookieSettings: "Cookie-Einstellungen",
      moreInfo: "Weitere Informationen",
      saveChoices: "Auswahl speichern",
      consentExpiryNotice: "Ihre Einwilligung läuft nach 6 Monaten ab, und Sie werden erneut gefragt.",
      dataProcessingNotice: "Einige Cookies beinhalten Datenverarbeitung durch Dritte."
    }
  };

  const t = content[language];

  useEffect(() => {}, [language]);

  useEffect(() => {
    const savedPreferences = getCookiePreferences();
    if (!savedPreferences) {
      setShowBanner(true);
    } else {
      applyCookieSettings(savedPreferences.settings);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    saveCookiePreferences(allAccepted, "banner");

    const bannerElement = document.querySelector('.cookie-banner-feedback');
    if (bannerElement) {
      bannerElement.textContent = "Preferences saved: All cookies accepted";
      setTimeout(() => {
        setShowBanner(false);
      }, 1000);
    } else {
      setShowBanner(false);
    }
  };

  const saveNecessaryOnly = () => {
    saveCookiePreferences(defaultSettings, "banner");

    const bannerElement = document.querySelector('.cookie-banner-feedback');
    if (bannerElement) {
      bannerElement.textContent = "Preferences saved: Only necessary cookies accepted";
      setTimeout(() => setShowBanner(false), 1000);
    } else {
      setShowBanner(false);
    }
  };

  // "Reject all" and "Necessary only" produce the same end state (necessary on, rest off)
  // but are exposed as separate buttons in the UI for clarity and GDPR best-practice.
  const rejectAll = saveNecessaryOnly;
  const acceptNecessary = saveNecessaryOnly;

  const openDetailedSettings = () => {
    setShowBanner(false);
    setShowDetailedSettings(true);
  };

  const toggleLanguage = () => {
    setLanguage(language === "en" ? "de" : "en");
  };

  return (
    <>
      {showBanner && (
        <div data-testid="cookie-banner" className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 m-4 rounded-lg shadow-lg max-w-screen-md w-full max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="p-6">
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-center">
                    <ShieldAlert className="h-6 w-6 text-amber-600 dark:text-amber-400 mr-2" />
                    <h2 className="text-xl font-semibold text-foreground">{t.title}</h2>
                  </div>
                  <Button
                    onClick={toggleLanguage}
                    variant="ghost"
                    size="sm"
                    className="ml-2 p-1"
                    title={language === "en" ? "Switch to German" : "Switch to English"}
                  >
                    <Globe className="h-4 w-4 mr-1" />
                    <span>{language === "en" ? "DE" : "EN"}</span>
                  </Button>
                </div>

                <Alert className="bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
                  <AlertDescription className="text-foreground">
                    {t.description}
                  </AlertDescription>
                </Alert>

                <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                  <p>{t.dataProcessingNotice}</p>
                  <p className="text-amber-600 dark:text-amber-400 text-xs">{t.consentExpiryNotice}</p>
                </div>

                <div className="cookie-banner-feedback text-center text-sm font-medium text-green-600 dark:text-green-400 h-5"></div>

                <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
                  <div className="col-span-2 sm:col-span-1 order-1 sm:order-1">
                    <Button variant="outline" onClick={openDetailedSettings} className="w-full bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                      <Settings className="h-4 w-4 mr-2" />
                      {language === "en" ? "Customize" : "Anpassen"}
                    </Button>
                  </div>
                  <div className="col-span-2 sm:col-span-1 order-2 sm:order-2">
                    <Button variant="outline" onClick={acceptNecessary} className="w-full bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 font-semibold">
                      <Check className="h-4 w-4 mr-2" />
                      {language === "en" ? "Necessary only" : "Nur notwendige"}
                    </Button>
                  </div>
                  <div className="col-span-1 order-3 sm:order-3">
                    <Button variant="outline" onClick={rejectAll} className="w-full bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                      <X className="h-4 w-4 mr-2" />
                      {language === "en" ? "Reject all" : "Alle ablehnen"}
                    </Button>
                  </div>
                  <div className="col-span-1 order-4 sm:order-4">
                    <Button onClick={acceptAll} className="w-full bg-[#9b87f5] hover:bg-[#8a76e4] text-white">
                      <Check className="h-4 w-4 mr-2" />
                      {language === "en" ? "Accept all" : "Alle akzeptieren"}
                    </Button>
                  </div>
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400 flex gap-4 justify-center">
                  <Link to="/privacy-policy" className="hover:text-[#9b87f5] flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    {language === "en" ? "Privacy Policy" : "Datenschutz"}
                  </Link>
                  <Link to="/terms-of-service" className="hover:text-[#9b87f5] flex items-center">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    {language === "en" ? "Terms of Service" : "Nutzungsbedingungen"}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <CookieSettingsDialog
        isOpen={showDetailedSettings}
        onOpenChange={setShowDetailedSettings}
      />
    </>
  );
};

interface CookieSettingsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const CookieSettingsDialog = ({ isOpen, onOpenChange }: CookieSettingsDialogProps) => {
  const [settings, setSettings] = useState<CookieSettings>(defaultSettings);
  const [activeTab, setActiveTab] = useState<string>("preferences");
  const { language, setLanguage } = useLanguage();
  const [consentExpiry, setConsentExpiry] = useState<number>(CONSENT_EXPIRY_DAYS);

  const content = {
    en: {
      title: "Cookie Settings",
      description: "Customize your cookie preferences. Necessary cookies are always enabled as they are essential for the website to function properly.",
      tabs: {
        preferences: "Preferences",
        information: "Information",
        legal: "Legal"
      },
      necessaryCookies: "Necessary Cookies",
      necessaryDescription: "Required for the basic functionality of the website. The website cannot function properly without these cookies.",
      functionalCookies: "Functional Cookies",
      functionalDescription: "Enable enhanced functionality and personalization. Without these cookies, certain features may not be available.",
      analyticalCookies: "Analytical Cookies",
      analyticalDescription: "Help us understand how visitors interact with our website. This helps us improve our services and user experience.",
      marketingCookies: "Marketing Cookies",
      marketingDescription: "Used to track visitors across websites to display relevant advertisements. They help make advertising more effective and relevant to you.",
      cookieDetails: "Cookie Details",
      cookieName: "Cookie Name",
      cookiePurpose: "Purpose",
      cookieDuration: "Duration",
      cookieProvider: "Provider",
      legalBasis: "Legal Basis",
      dataController: "Data Controller",
      privacyPolicy: "Privacy Policy",
      legalTitle: "Legal Information",
      legalText: "According to the ePrivacy Directive and GDPR, we require your consent before storing cookies on your device, except for those cookies that are strictly necessary for the functionality of this website. Your consent is voluntary and can be withdrawn at any time.",
      thirdPartyData: "Third-Party Data Transfers",
      thirdPartyText: "Some cookies may involve data transfers to countries outside the European Economic Area (EEA). By accepting these cookies, you consent to such transfers where appropriate safeguards have been implemented.",
      rightToWithdraw: "Right to Withdraw Consent",
      rightToWithdrawText: "You can withdraw your consent at any time by using the Cookie Settings button available on our website.",
      consentExpiry: "Consent Expiry",
      consentExpiryText: "Your consent will expire after 6 months, at which point you will be asked to renew your cookie preferences.",
      cancel: "Cancel",
      save: "Save Settings",
      acceptAll: "Accept All",
      rejectAll: "Reject All",
      switchLanguage: "Switch language",
      dataProtectionOfficer: "Privacy Contact",
      dpoContact: "You can contact our privacy team at privacy@buyfors.com with any questions related to your data and cookies."
    },
    de: {
      title: "Cookie-Einstellungen",
      description: "Passen Sie Ihre Cookie-Einstellungen an. Notwendige Cookies sind immer aktiviert, da sie für die ordnungsgemäße Funktion der Website unerlässlich sind.",
      tabs: {
        preferences: "Einstellungen",
        information: "Informationen",
        legal: "Rechtliche"
      },
      necessaryCookies: "Notwendige Cookies",
      necessaryDescription: "Erforderlich für die grundlegende Funktionalität der Website. Die Website kann ohne diese Cookies nicht richtig funktionieren.",
      functionalCookies: "Funktionale Cookies",
      functionalDescription: "Ermöglichen erweiterte Funktionen und Personalisierung. Ohne diese Cookies sind bestimmte Funktionen möglicherweise nicht verfügbar.",
      analyticalCookies: "Analytische Cookies",
      analyticalDescription: "Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren. Dies hilft uns, unsere Dienste und Benutzererfahrung zu verbessern.",
      marketingCookies: "Marketing-Cookies",
      marketingDescription: "Werden verwendet, um Besucher über Websites hinweg zu verfolgen und relevante Werbung anzuzeigen. Sie tragen dazu bei, dass Werbung effektiver und relevanter für Sie ist.",
      cookieDetails: "Cookie-Details",
      cookieName: "Cookie-Name",
      cookiePurpose: "Zweck",
      cookieDuration: "Dauer",
      cookieProvider: "Anbieter",
      legalBasis: "Rechtsgrundlage",
      dataController: "Datenverantwortlicher",
      privacyPolicy: "Datenschutzrichtlinie",
      legalTitle: "Rechtliche Informationen",
      legalText: "Gemäß der ePrivacy-Richtlinie und der DSGVO benötigen wir Ihre Einwilligung, bevor wir Cookies auf Ihrem Gerät speichern, mit Ausnahme jener Cookies, die für die Funktionalität dieser Website unbedingt erforderlich sind. Ihre Einwilligung ist freiwillig und kann jederzeit widerrufen werden.",
      thirdPartyData: "Übermittlung von Daten an Drittländer",
      thirdPartyText: "Einige Cookies können Datenübertragungen in Länder außerhalb des Europäischen Wirtschaftsraums (EWR) beinhalten. Durch die Akzeptanz dieser Cookies stimmen Sie solchen Übertragungen zu, sofern geeignete Schutzmaßnahmen implementiert wurden.",
      rightToWithdraw: "Recht auf Widerruf der Einwilligung",
      rightToWithdrawText: "Sie können Ihre Einwilligung jederzeit widerrufen, indem Sie die Cookie-Einstellungen-Schaltfläche auf unserer Website verwenden.",
      consentExpiry: "Ablauf der Einwilligung",
      consentExpiryText: "Ihre Einwilligung läuft nach 6 Monaten ab, danach werden Sie gebeten, Ihre Cookie-Präferenzen zu erneuern.",
      cancel: "Abbrechen",
      save: "Einstellungen speichern",
      acceptAll: "Alle akzeptieren",
      rejectAll: "Alle ablehnen",
      switchLanguage: "Sprache wechseln",
      dataProtectionOfficer: "Datenschutz-Kontakt",
      dpoContact: "Sie können unser Datenschutz-Team unter privacy@buyfors.com kontaktieren, wenn Sie Fragen zu Ihren Daten und Cookies haben."
    }
  };

  const t = content[language];

  useEffect(() => {}, [language]);

  useEffect(() => {
    if (isOpen) {
      const savedPreferences = getCookiePreferences();
      if (savedPreferences) {
        setSettings(savedPreferences.settings);
      } else {
        setSettings(defaultSettings);
      }
    }
  }, [isOpen]);

  const handleSave = () => {
    saveCookiePreferences(settings, "detailed");

    const feedbackMessage = document.createElement('div');
    feedbackMessage.className = 'text-green-600 text-center py-2 font-medium';
    feedbackMessage.textContent = language === "en"
      ? "Your preferences have been saved"
      : "Ihre Einstellungen wurden gespeichert";

    const dialogContent = document.querySelector('.dialog-content');
    if (dialogContent) {
      dialogContent.appendChild(feedbackMessage);
      setTimeout(() => {
        onOpenChange(false);
      }, 1000);
    } else {
      onOpenChange(false);
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    saveCookiePreferences(allAccepted, "detailed");
    setSettings(allAccepted);

    const feedbackMessage = document.createElement('div');
    feedbackMessage.className = 'text-green-600 text-center py-2 font-medium';
    feedbackMessage.textContent = language === "en"
      ? "All cookies accepted"
      : "Alle Cookies akzeptiert";

    const dialogContent = document.querySelector('.dialog-content');
    if (dialogContent) {
      dialogContent.appendChild(feedbackMessage);
      setTimeout(() => {
        onOpenChange(false);
      }, 1000);
    } else {
      onOpenChange(false);
    }
  };

  const handleRejectAll = () => {
    saveCookiePreferences(defaultSettings, "detailed");
    setSettings({...defaultSettings});

    const feedbackMessage = document.createElement('div');
    feedbackMessage.className = 'text-green-600 text-center py-2 font-medium';
    feedbackMessage.textContent = language === "en"
      ? "Only necessary cookies accepted"
      : "Nur notwendige Cookies akzeptiert";

    const dialogContent = document.querySelector('.dialog-content');
    if (dialogContent) {
      dialogContent.appendChild(feedbackMessage);
      setTimeout(() => {
        onOpenChange(false);
      }, 1000);
    } else {
      onOpenChange(false);
    }
  };

  const handleChange = (key: keyof CookieSettings) => (value: boolean) => {
    if (key === 'necessary') return;
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleLanguage = () => {
    setLanguage(language === "en" ? "de" : "en");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto dialog-content w-[95vw] mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{t.title}</span>
            <Button
              onClick={toggleLanguage}
              variant="ghost"
              size="sm"
              className="ml-2 p-1"
              title={t.switchLanguage}
            >
              <Globe className="h-4 w-4 mr-1" />
              <span>{language === "en" ? "DE" : "EN"}</span>
            </Button>
          </DialogTitle>
          <DialogDescription>
            {t.description}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="preferences" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3">
            <TabsTrigger value="preferences">{t.tabs.preferences}</TabsTrigger>
            <TabsTrigger value="information">{t.tabs.information}</TabsTrigger>
            <TabsTrigger value="legal">{t.tabs.legal}</TabsTrigger>
          </TabsList>

          <TabsContent value="preferences" className="py-4 space-y-6">
            <Alert className="bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
              <AlertTitle className="text-blue-800 dark:text-blue-200">
                {language === "en" ? "Consent Expiry Notice" : "Hinweis zum Ablauf der Einwilligung"}
              </AlertTitle>
              <AlertDescription className="text-blue-700 dark:text-blue-300">
                {language === "en" ?
                  "Your cookie preferences will expire after 6 months for privacy protection, after which you'll be asked to review them again." :
                  "Ihre Cookie-Einstellungen laufen nach 6 Monaten zum Datenschutz ab. Danach werden Sie gebeten, diese erneut zu überprüfen."}
              </AlertDescription>
            </Alert>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="necessary" className="text-base font-medium">{t.necessaryCookies}</Label>
                  <Badge variant="outline" className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                    {language === "en" ? "Required" : "Erforderlich"}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.necessaryDescription}
                </p>
              </div>
              <Switch
                id="necessary"
                checked={settings.necessary}
                disabled={true}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="functional" className="text-base font-medium">{t.functionalCookies}</Label>
                  <Badge variant="outline" className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600">
                    {language === "en" ? "Optional" : "Optional"}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.functionalDescription}
                </p>
              </div>
              <Switch
                id="functional"
                checked={settings.functional}
                onCheckedChange={(checked) => handleChange('functional')(checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="analytics" className="text-base font-medium">{t.analyticalCookies}</Label>
                  <Badge variant="outline" className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600">
                    {language === "en" ? "Optional" : "Optional"}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.analyticalDescription}
                </p>
              </div>
              <Switch
                id="analytics"
                checked={settings.analytics}
                onCheckedChange={(checked) => handleChange('analytics')(checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="marketing" className="text-base font-medium">{t.marketingCookies}</Label>
                  <Badge variant="outline" className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600">
                    {language === "en" ? "Optional" : "Optional"}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.marketingDescription}
                </p>
              </div>
              <Switch
                id="marketing"
                checked={settings.marketing}
                onCheckedChange={(checked) => handleChange('marketing')(checked)}
              />
            </div>
          </TabsContent>

          <TabsContent value="information" className="py-4">
            <h3 className="font-medium text-lg mb-4">{t.cookieDetails}</h3>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="necessary">
                <AccordionTrigger>
                  <div className="flex items-center">
                    <Info className="h-4 w-4 mr-2" />
                    {t.necessaryCookies}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-x-auto -mx-6 sm:mx-0">
                    <div className="inline-block min-w-full align-middle px-6 sm:px-0">
                      <div className="overflow-hidden border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieName}</th>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookiePurpose}</th>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieDuration}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {cookieDetails.necessary.map((cookie, index) => (
                              <tr key={index}>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 break-words">{cookie.name}</td>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 break-words">{cookie.purpose}</td>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{cookie.duration}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="functional">
                <AccordionTrigger>
                  <div className="flex items-center">
                    <Info className="h-4 w-4 mr-2" />
                    {t.functionalCookies}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-x-auto -mx-6 sm:mx-0">
                    <div className="inline-block min-w-full align-middle px-6 sm:px-0">
                      <div className="overflow-hidden border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieName}</th>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookiePurpose}</th>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieDuration}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {cookieDetails.functional.map((cookie, index) => (
                              <tr key={index}>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 break-words">{cookie.name}</td>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 break-words">{cookie.purpose}</td>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{cookie.duration}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="analytics">
                <AccordionTrigger>
                  <div className="flex items-center">
                    <Info className="h-4 w-4 mr-2" />
                    {t.analyticalCookies}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-x-auto -mx-6 sm:mx-0">
                    <div className="inline-block min-w-full align-middle px-6 sm:px-0">
                      <div className="overflow-hidden border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieName}</th>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookiePurpose}</th>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieDuration}</th>
                              <th scope="col" className="hidden sm:table-cell px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieProvider}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {cookieDetails.analytics.map((cookie, index) => (
                              <tr key={index}>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 break-words">{cookie.name}</td>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 break-words">{cookie.purpose}</td>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{cookie.duration}</td>
                                <td className="hidden sm:table-cell px-2 py-3 text-xs text-gray-700 dark:text-gray-300">
                                  {cookie.provider}
                                  {cookie.privacyPolicy && (
                                    <a href={cookie.privacyPolicy} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 dark:text-blue-400 hover:underline">
                                      <ExternalLink className="h-3 w-3 inline" />
                                    </a>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                    <p className="font-medium mb-1">{t.dataController}:</p>
                    <ul className="space-y-1">
                      {Array.from(new Set(cookieDetails.analytics.map(cookie => cookie.dataController))).map((controller, idx) => (
                        <li key={idx}>{controller}</li>
                      ))}
                    </ul>
                    <p className="font-medium mt-2 mb-1">{t.privacyPolicy}:</p>
                    <ul className="space-y-1">
                      {Array.from(new Set(cookieDetails.analytics.filter(cookie => cookie.privacyPolicy).map(cookie => ({
                        controller: cookie.dataController,
                        policy: cookie.privacyPolicy
                      })))).map((item, idx) => (
                        <li key={idx}>
                          {item.controller}:&nbsp;
                          <a href={item.policy} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                            {language === "en" ? "View" : "Ansehen"}
                            <ExternalLink className="h-3 w-3 inline ml-1" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="marketing">
                <AccordionTrigger>
                  <div className="flex items-center">
                    <Info className="h-4 w-4 mr-2" />
                    {t.marketingCookies}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-x-auto -mx-6 sm:mx-0">
                    <div className="inline-block min-w-full align-middle px-6 sm:px-0">
                      <div className="overflow-hidden border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-fixed">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieName}</th>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookiePurpose}</th>
                              <th scope="col" className="px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieDuration}</th>
                              <th scope="col" className="hidden sm:table-cell px-2 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase text-left">{t.cookieProvider}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {cookieDetails.marketing.map((cookie, index) => (
                              <tr key={index}>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 break-words">{cookie.name}</td>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 break-words">{cookie.purpose}</td>
                                <td className="px-2 py-3 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{cookie.duration}</td>
                                <td className="hidden sm:table-cell px-2 py-3 text-xs text-gray-700 dark:text-gray-300">
                                  {cookie.provider}
                                  {cookie.privacyPolicy && (
                                    <a href={cookie.privacyPolicy} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 dark:text-blue-400 hover:underline">
                                      <ExternalLink className="h-3 w-3 inline" />
                                    </a>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                    <p className="font-medium mb-1">{t.dataController}:</p>
                    <ul className="space-y-1">
                      {Array.from(new Set(cookieDetails.marketing.map(cookie => cookie.dataController))).map((controller, idx) => (
                        <li key={idx}>{controller}</li>
                      ))}
                    </ul>
                    <p className="font-medium mt-2 mb-1">{t.privacyPolicy}:</p>
                    <ul className="space-y-1">
                      {Array.from(new Set(cookieDetails.marketing.filter(cookie => cookie.privacyPolicy).map(cookie => ({
                        controller: cookie.dataController,
                        policy: cookie.privacyPolicy
                      })))).map((item, idx) => (
                        <li key={idx}>
                          {item.controller}:&nbsp;
                          <a href={item.policy} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                            {language === "en" ? "View" : "Ansehen"}
                            <ExternalLink className="h-3 w-3 inline ml-1" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>

          <TabsContent value="legal" className="py-4 space-y-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-lg mb-2">{t.legalTitle}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t.legalText}
                </p>
              </div>

              <div>
                <h3 className="font-medium text-lg mb-2">{t.thirdPartyData}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t.thirdPartyText}
                </p>
              </div>

              <div>
                <h3 className="font-medium text-lg mb-2">{t.rightToWithdraw}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t.rightToWithdrawText}
                </p>
              </div>

              <div>
                <h3 className="font-medium text-lg mb-2">{t.consentExpiry}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t.consentExpiryText}
                </p>
              </div>

              <div>
                <h3 className="font-medium text-lg mb-2">{t.dataProtectionOfficer}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {t.dpoContact}
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="sm:mr-auto">
            {t.cancel}
          </Button>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleRejectAll}>
              {t.rejectAll}
            </Button>
            <Button variant="outline" onClick={handleAcceptAll}>
              {t.acceptAll}
            </Button>
            <Button onClick={handleSave} className="bg-[#9b87f5] hover:bg-[#8a76e4] text-white">
              {t.save}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CookieSettingsButton = ({ compact = false }: { compact?: boolean }) => {
  const [showSettings, setShowSettings] = useState(false);
  const { language } = useLanguage();

  return (
    <>
      <Button
        onClick={() => setShowSettings(true)}
        variant={compact ? "default" : "outline"}
        size={compact ? "icon" : "default"}
        className={compact ? "rounded-full shadow-lg bg-[#9b87f5] hover:bg-[#8a76e4]" : ""}
        aria-label={language === "en" ? "Cookie Settings" : "Cookie-Einstellungen"}
      >
        {compact ? (
          <Cookie className="h-5 w-5 text-white" />
        ) : (
          <>
            <Cookie className="h-4 w-4 mr-2" />
            <span>{language === "en" ? "Cookie Settings" : "Cookie-Einstellungen"}</span>
          </>
        )}
      </Button>
      <CookieSettingsDialog
        isOpen={showSettings}
        onOpenChange={setShowSettings}
      />
    </>
  );
};
